<template>
  <section :id="sectionId" class="relative mx-auto max-w-7xl px-6 py-16 md:py-24">
    <div class="flex items-end justify-between gap-4">
      <h2
        class="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-900"
      >
        {{ title }}
      </h2>
      <div class="flex gap-2 shrink-0">
        <button
          type="button"
          @click="setViewMode('grid')"
          class="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm ring-1 transition"
          :class="viewMode === 'grid'
            ? 'bg-neutral-900 text-white ring-neutral-900'
            : 'bg-white text-neutral-700 ring-neutral-200 hover:bg-neutral-50 hover:ring-neutral-300'"
        >
          <Grid3x3 :size="16" />
          Grid
        </button>
        <button
          type="button"
          @click="setViewMode('carousel')"
          class="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm ring-1 transition"
          :class="viewMode === 'carousel'
            ? 'bg-neutral-900 text-white ring-neutral-900'
            : 'bg-white text-neutral-700 ring-neutral-200 hover:bg-neutral-50 hover:ring-neutral-300'"
        >
          <Rows :size="16" />
          Carousel
        </button>
      </div>
    </div>

    <!-- Framed board -->
    <div
      class="mt-10 rounded-[28px] p-2 bg-gradient-to-b from-white to-white/60 ring-1 ring-neutral-200"
    >
      <div
        class="rounded-[22px] p-6 md:p-8 bg-white/80 backdrop-blur-sm ring-1 ring-neutral-200/60"
      >
        <!-- GRID VIEW -->
        <div v-if="viewMode === 'grid'" class="space-y-6">
          <div
            v-if="gridProjects.length"
            class="grid md:grid-cols-12 gap-6 items-start"
          >
            <WorkProjectCard
              v-for="(project, index) in gridProjects"
              :key="project.id || index"
              :project="project"
              :image-class="imageClassFor(project)"
              :root-class="gridClassFor(project)"
            />
          </div>

          <div
            v-if="hasMasonry"
            class="grid md:grid-cols-2 gap-6 items-start"
          >
            <div class="flex flex-col gap-6">
              <WorkProjectCard
                v-for="(project, index) in leftProjects"
                :key="project.id || `left-${index}`"
                :project="project"
                :image-class="imageClassFor(project)"
              />
            </div>
            <div class="flex flex-col gap-6">
              <WorkProjectCard
                v-for="(project, index) in rightProjects"
                :key="project.id || `right-${index}`"
                :project="project"
                :image-class="imageClassFor(project)"
              />
            </div>
          </div>
        </div>

        <!-- CAROUSEL VIEW -->
        <div v-else class="relative">
          <div
            ref="carousel"
            class="work-carousel flex gap-5 overflow-x-auto pb-1"
            @scroll.passive="onCarouselScroll"
            @wheel.passive="onUserInteract"
            @touchstart.passive="onUserInteract"
            @pointerdown="onUserInteract"
          >
            <WorkProjectCard
              v-for="(slide, index) in loopSlides"
              :key="`${slide.id || slide.title}-${index}`"
              :project="slide"
              fill
              root-class="shrink-0"
              @sized="onSlideSized"
            />
          </div>

          <div class="mt-6 flex items-center justify-between gap-4">
            <div class="flex gap-2">
              <button
                type="button"
                @click="onManualNav(-1)"
                class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-neutral-200 text-neutral-800 hover:bg-neutral-50 hover:ring-neutral-300 transition"
                aria-label="Previous project"
              >
                <ChevronLeft :size="18" />
              </button>
              <button
                type="button"
                @click="onManualNav(1)"
                class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-neutral-200 text-neutral-800 hover:bg-neutral-50 hover:ring-neutral-300 transition"
                aria-label="Next project"
              >
                <ChevronRight :size="18" />
              </button>
            </div>

            <div class="flex items-center gap-2">
              <button
                v-for="(project, index) in projects"
                :key="project.id || `dot-${index}`"
                type="button"
                class="h-2 rounded-full transition-all"
                :class="activeDot === index ? 'w-6 bg-neutral-900' : 'w-2 bg-neutral-300 hover:bg-neutral-400'"
                :aria-label="`Go to ${project.title}`"
                @click="onDotClick(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Process strip with icons -->
    <div v-if="showProcess" class="mt-12 grid sm:grid-cols-3 gap-4">
      <div
        v-for="(process, index) in processes"
        :key="index"
        class="rounded-xl bg-white ring-1 ring-neutral-200 p-4 flex items-center gap-3 hover:ring-neutral-300 transition"
      >
        <component :is="getIconComponent(process.icon)" :size="20" :class="process.iconColor" />
        <div>
          <p class="text-neutral-900 text-[15px]">{{ process.title }}</p>
          <p class="text-xs text-neutral-600">{{ process.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Grid3x3, Rows, ChevronLeft, ChevronRight, Search, Presentation, Rocket } from 'lucide-vue-next'
import WorkProjectCard from './WorkProjectCard.vue'

const LAYOUTS = {
  featuredLeft: {
    grid: 'md:col-span-5',
    image: ''
  },
  featuredRight: {
    grid: 'md:col-span-7',
    image: ''
  },
  card: {
    grid: 'md:col-span-4',
    image: ''
  },
  logoLight: {
    grid: 'md:col-span-4',
    image: 'bg-neutral-50 p-8'
  },
  logoDark: {
    grid: 'md:col-span-4',
    image: 'bg-neutral-950 p-6'
  },
  poster: {
    grid: 'md:col-span-6',
    image: ''
  }
}

const SCROLL_SPEED = 55 // px per second — constant smooth marquee
const RESUME_MS = 1800

export default {
  name: 'UIUXWork',
  components: {
    Grid3x3,
    Rows,
    ChevronLeft,
    ChevronRight,
    Search,
    Presentation,
    Rocket,
    WorkProjectCard
  },
  props: {
    sectionId: {
      type: String,
      default: 'work'
    },
    title: {
      type: String,
      default: 'UI/UX'
    },
    showProcess: {
      type: Boolean,
      default: true
    },
    projects: {
      type: Array,
      default: () => []
    },
    processes: {
      type: Array,
      default: () => [
        {
          title: 'Discovery',
          description: 'Research, insights, synthesis',
          icon: 'Search',
          iconColor: 'text-emerald-600'
        },
        {
          title: 'Design',
          description: 'Flows, wireframes, systems',
          icon: 'Presentation',
          iconColor: 'text-sky-600'
        },
        {
          title: 'Delivery',
          description: 'Prototypes, specs, QA',
          icon: 'Rocket',
          iconColor: 'text-amber-600'
        }
      ]
    }
  },
  data() {
    return {
      viewMode: 'carousel',
      activeSlide: 0,
      rafId: null,
      resumeTimer: null,
      lastTs: 0,
      isJumping: false,
      carouselReady: false,
      autoplayPaused: false
    }
  },
  computed: {
    gridProjects() {
      return this.projects.filter((project) => !project.column)
    },
    leftProjects() {
      return this.projects.filter((project) => project.column === 'left')
    },
    rightProjects() {
      return this.projects.filter((project) => project.column === 'right')
    },
    hasMasonry() {
      return this.leftProjects.length > 0 || this.rightProjects.length > 0
    },
    loopSlides() {
      if (!this.projects.length) return []
      return [...this.projects, ...this.projects, ...this.projects]
    },
    setSize() {
      return this.projects.length
    },
    activeDot() {
      if (!this.setSize) return 0
      return ((this.activeSlide % this.setSize) + this.setSize) % this.setSize
    }
  },
  watch: {
    viewMode(mode) {
      this.stopAutoplay()
      if (mode === 'carousel') {
        this.$nextTick(() => this.initCarousel())
      }
    },
    projects() {
      if (this.viewMode === 'carousel') {
        this.$nextTick(() => this.initCarousel())
      }
    }
  },
  mounted() {
    if (this.viewMode === 'carousel') {
      this.$nextTick(() => this.initCarousel())
    }
  },
  beforeUnmount() {
    this.stopAutoplay()
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer)
      this.resumeTimer = null
    }
  },
  methods: {
    layoutFor(project) {
      return LAYOUTS[project.layout] || LAYOUTS.card
    },
    gridClassFor(project) {
      return this.layoutFor(project).grid
    },
    imageClassFor(project) {
      return this.layoutFor(project).image
    },
    setViewMode(mode) {
      this.viewMode = mode
    },
    getSetWidth() {
      const el = this.$refs.carousel
      if (!el || !this.setSize) return 0
      const first = el.children[0]
      const second = el.children[this.setSize]
      if (!first || !second) return 0
      return second.offsetLeft - first.offsetLeft
    },
    initCarousel() {
      const el = this.$refs.carousel
      if (!el || !this.setSize) return

      this.carouselReady = false
      this.isJumping = true
      const startIndex = this.setSize
      const slide = el.children[startIndex]
      if (slide) {
        el.scrollLeft = slide.offsetLeft
      }
      this.activeSlide = startIndex
      this.isJumping = false
      this.carouselReady = true
      this.updateCarouselState()
      this.startAutoplay()
    },
    onSlideSized() {
      if (this.viewMode !== 'carousel' || !this.carouselReady) return
      const el = this.$refs.carousel
      const slide = el?.children[this.setSize]
      if (!el || !slide || this.rafId) return
      this.isJumping = true
      el.scrollLeft = slide.offsetLeft
      this.isJumping = false
    },
    startAutoplay() {
      this.stopAutoplay()
      if (this.viewMode !== 'carousel' || this.setSize < 2) return
      this.autoplayPaused = false
      this.lastTs = 0
      const tick = (ts) => {
        if (this.autoplayPaused || this.viewMode !== 'carousel') {
          this.rafId = null
          return
        }
        const el = this.$refs.carousel
        if (!el) {
          this.rafId = null
          return
        }
        if (!this.lastTs) this.lastTs = ts
        const dt = Math.min(ts - this.lastTs, 32)
        this.lastTs = ts
        el.scrollLeft += (SCROLL_SPEED * dt) / 1000
        this.normalizeLoopByScroll()
        this.updateCarouselState()
        this.rafId = requestAnimationFrame(tick)
      }
      this.rafId = requestAnimationFrame(tick)
    },
    stopAutoplay() {
      this.autoplayPaused = true
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      this.lastTs = 0
    },
    onUserInteract() {
      this.stopAutoplay()
      if (this.resumeTimer) clearTimeout(this.resumeTimer)
      this.resumeTimer = setTimeout(() => {
        this.startAutoplay()
      }, RESUME_MS)
    },
    onManualNav(direction) {
      this.scrollCarousel(direction)
      this.onUserInteract()
    },
    onDotClick(index) {
      this.goToLogicalSlide(index)
      this.onUserInteract()
    },
    scrollCarousel(direction) {
      const el = this.$refs.carousel
      if (!el) return
      const nextIndex = Math.min(
        Math.max(this.activeSlide + direction, 0),
        el.children.length - 1
      )
      const next = el.children[nextIndex]
      if (!next) return
      el.scrollTo({ left: next.offsetLeft, behavior: 'smooth' })
      this.activeSlide = nextIndex
    },
    goToLogicalSlide(logicalIndex) {
      const el = this.$refs.carousel
      if (!el || !this.setSize) return
      const index = this.setSize + logicalIndex
      const slide = el.children[index]
      if (!slide) return
      el.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' })
      this.activeSlide = index
    },
    onCarouselScroll() {
      if (this.isJumping) return
      this.updateCarouselState()
      if (this.autoplayPaused) {
        this.normalizeLoopByScroll()
      }
    },
    updateCarouselState() {
      const el = this.$refs.carousel
      if (!el) return

      const slides = Array.from(el.children)
      if (!slides.length) return

      const center = el.scrollLeft + el.clientWidth / 2
      let closest = 0
      let closestDist = Infinity
      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2
        const dist = Math.abs(center - slideCenter)
        if (dist < closestDist) {
          closestDist = dist
          closest = index
        }
      })
      this.activeSlide = closest
    },
    normalizeLoopByScroll() {
      const el = this.$refs.carousel
      if (!el || !this.setSize || this.isJumping) return
      const setWidth = this.getSetWidth()
      if (!setWidth) return

      const middleStart = el.children[this.setSize]?.offsetLeft ?? setWidth
      const thirdStart = el.children[this.setSize * 2]?.offsetLeft ?? setWidth * 2

      if (el.scrollLeft >= thirdStart) {
        this.isJumping = true
        el.scrollLeft -= setWidth
        this.isJumping = false
      } else if (el.scrollLeft < middleStart - setWidth * 0.5) {
        this.isJumping = true
        el.scrollLeft += setWidth
        this.isJumping = false
      }
    },
    getIconComponent(iconName) {
      const iconMap = {
        Search,
        Presentation,
        Rocket
      }
      return iconMap[iconName] || Search
    }
  }
}
</script>

<style scoped>
.work-carousel {
  scrollbar-width: thin;
  scrollbar-color: #d4d4d4 transparent;
}

.work-carousel::-webkit-scrollbar {
  height: 6px;
}

.work-carousel::-webkit-scrollbar-thumb {
  background: #d4d4d4;
  border-radius: 999px;
}
</style>
